import apiReq from 'core/api';
import type { AdminUser, RoleRef } from 'types';

/** Сырой пользователь, как его отдаёт бэкенд (`/user/all`, `/user/{id}`). */
export interface BackendUser {
  id: string | number;
  email?: string;
  name?: string | null;
  is_active?: boolean;
  is_admin?: boolean;
  created_at?: number;
  has_yandex_access?: boolean;
  has_confluence_access?: boolean;
  has_jira_access?: boolean;
  roles?: Array<{ id: string | number; name: string; title?: string | null }>;
}

/** Тело форм `CreateUserForm` / `UpdateUserForm` (без `id`). */
export interface UserFormPayload {
  email: string;
  name: string;
  is_active: boolean;
  is_admin: boolean;
  /** Список UUID ролей. Не может быть пустым (требование API). */
  roles: string[];
  yandex_tracker_queues?: string[];
}

/** Инициалы для аватара из ФИО. */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name.trim().slice(0, 2) || '—').toUpperCase();
}

/** Маппинг сырого пользователя бэкенда в доменную модель `AdminUser`. */
export function mapBackendUser(u: BackendUser): AdminUser {
  const name = u.name ?? '';
  const roles: RoleRef[] = (u.roles ?? []).map((r) => ({
    id: String(r.id),
    name: r.name,
    title: r.title ?? null,
  }));
  return {
    id: String(u.id),
    name,
    email: u.email ?? '',
    isActive: u.is_active !== false,
    isAdmin: u.is_admin === true || roles.some((r) => r.name === 'admin'),
    createdAt: typeof u.created_at === 'number' ? u.created_at : null,
    roles,
    hasYandexAccess: u.has_yandex_access === true,
    hasConfluenceAccess: u.has_confluence_access === true,
    hasJiraAccess: u.has_jira_access === true,
    avatarInitials: getInitials(name),
  };
}

type UsersResponse = BackendUser[] | { users?: BackendUser[]; items?: BackendUser[] };

/** `GET /user/all` — список всех пользователей. */
export async function getUsers(): Promise<AdminUser[]> {
  const res = await apiReq<UsersResponse>('user/all');
  const list = Array.isArray(res) ? res : (res?.users ?? res?.items ?? []);
  return list.map(mapBackendUser);
}

/** `GET /user/{id}` — информация о пользователе. */
export async function getUser(id: string): Promise<AdminUser | undefined> {
  const res = await apiReq<BackendUser>(`user/${id}`);
  return res ? mapBackendUser(res) : undefined;
}

/** `POST /user/create` — создать пользователя. */
export function createUser(payload: UserFormPayload): Promise<unknown> {
  return apiReq('user/create', 'POST', payload);
}

/** `POST /user/update` — обновить пользователя. */
export function updateUser(id: string, payload: UserFormPayload): Promise<unknown> {
  return apiReq('user/update', 'POST', { id, ...payload });
}

/** `DELETE /user/delete?user_id=` — удалить пользователя. */
export function deleteUser(id: string): Promise<unknown> {
  return apiReq(`user/delete?user_id=${encodeURIComponent(id)}`, 'DELETE');
}
